import { Card, Form, HookForm } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { BigNumber } from "bignumber.js";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { TbPlus } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { MORTGAGE_TYPES, NewPurchaseBodySchema } from "shared";
import { AddressSelect } from "@/addresses/AddressSelect";
import { BorrowerSelect } from "@/borrower/BorrowerSelect";
import { Button } from "@/components/Button";
import { PriceInput, Transformers } from "@/components/form/PriceInput";
import { Container } from "@/components/grid/Container";
import { LinkGen } from "@/LinkGen";
import { AppPage } from "@/layout/AppLayout";
import { usePurchaseActions } from "@/purchases/hooks";

export const PagePurchaseNew = () => {
  const navigate = useNavigate();
  const { create } = usePurchaseActions();

  const [isTBD, setIsTBD] = useState(true);
  const { control, handleSubmit, register, watch, setValue, formState } =
    useForm({
      resolver: zodResolver(NewPurchaseBodySchema),
      reValidateMode: "onBlur",
      defaultValues: {
        addressID: null,
        annualTax: null,
      },
    });

  const mortgageType = watch("mortgageType");
  const priceRaw = watch("price") as number;
  const price = useMemo(() => new BigNumber(priceRaw || 0), [priceRaw]);

  const taxModes = useMemo(
    () => ({
      annual: {
        dp: 2,
        label: "Yr",
        transformer: Transformers.Base(),
        tooltip: "Per year",
      },
      monthly: {
        dp: 2,
        label: "Mo",
        transformer: Transformers.Fraction(12),
        tooltip: "Per month",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price per year",
      },
    }),
    [price],
  );

  const insuranceModes = useMemo(
    () => ({
      annual: {
        dp: 2,
        label: "Yr",
        transformer: Transformers.Base(),
        tooltip: "Per year",
      },
      monthly: {
        dp: 2,
        label: "Mo",
        transformer: Transformers.Fraction(12),
        tooltip: "Per month",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price per year",
      },
    }),
    [price],
  );

  const downpaymentModes = useMemo(
    () => ({
      flat: {
        dp: 2,
        label: "$",
        transformer: Transformers.Base(),
        tooltip: "Flat amount",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price",
      },
    }),
    [price],
  );

  const hoaModes = useMemo(
    () => ({
      annual: {
        dp: 2,
        label: "Yr",
        transformer: Transformers.Base(),
        tooltip: "Per year",
      },
      monthly: {
        dp: 2,
        label: "Mo",
        transformer: Transformers.Fraction(12),
        tooltip: "Per month",
      },
      percentage: {
        dp: 2,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price per year",
      },
    }),
    [price],
  );

  const onSubmit = handleSubmit(async (values) => {
    await create(values);
    navigate(LinkGen.Purchase());
  });

  return (
    <AppPage title="New Purchase">
      <Container>
        <form onSubmit={onSubmit}>
          <Card>
            <Card.Header>New Purchase</Card.Header>
            <Card.Body>
              <Card.Title>Customer information</Card.Title>
              <HookForm.Group
                control={control}
                name="borrowerID"
                className="mb-3"
              >
                <HookForm.Label required>Borrower</HookForm.Label>
                <BorrowerSelect
                  placeholder="Search borrower..."
                  control={control}
                  name="borrowerID"
                />
              </HookForm.Group>
              <Form.Group>
                <Form.Label>TBD address</Form.Label>
                <input
                  type="checkbox"
                  className="mb-3"
                  checked={isTBD}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setIsTBD(next);
                    setValue("addressID", null);
                    setValue("annualTax", null);
                  }}
                />
              </Form.Group>
              {isTBD ? (
                <HookForm.Group
                  control={control}
                  name="annualTax"
                  className="mb-3"
                >
                  <HookForm.Label required>Annual tax</HookForm.Label>
                  <PriceInput
                    placeholder="Tax amount"
                    control={control}
                    name="annualTax"
                    modes={taxModes}
                    defaultMode="percentage"
                    defaultTyped={0.6}
                  />
                </HookForm.Group>
              ) : (
                <HookForm.Group control={control} name="addressID">
                  <HookForm.Label required>Address</HookForm.Label>
                  <AddressSelect
                    placeholder="Search address..."
                    control={control}
                    name="addressID"
                  />
                </HookForm.Group>
              )}
            </Card.Body>
            <Card.Body>
              <Card.Title className="mb-2">Purchase details</Card.Title>
              <HookForm.Group
                control={control}
                name="mortgageType"
                className="mb-3"
              >
                <HookForm.Label required>Mortgage type</HookForm.Label>
                <HookForm.Select control={control} name="mortgageType">
                  <option value="">Select type...</option>
                  {MORTGAGE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </HookForm.Select>
              </HookForm.Group>
              {mortgageType === "VA" && (
                <>
                  <HookForm.Group
                    control={control}
                    name="vaFirstTime"
                    className="mb-3"
                  >
                    <HookForm.Label required>VA first time use</HookForm.Label>
                    <Form.Select
                      error={!!formState.errors.vaFirstTime}
                      defaultValue=""
                      {...register("vaFirstTime", {
                        setValueAs: (value) =>
                          value === "" ? null : value === "true",
                      })}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Select>
                  </HookForm.Group>
                  <HookForm.Group
                    control={control}
                    name="vaExempt"
                    className="mb-3"
                  >
                    <HookForm.Label required>
                      Exempt (Disability rate 10%+)
                    </HookForm.Label>
                    <Form.Select
                      error={!!formState.errors.vaExempt}
                      defaultValue=""
                      {...register("vaExempt", {
                        setValueAs: (value) =>
                          value === "" ? null : value === "true",
                      })}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Form.Select>
                  </HookForm.Group>
                </>
              )}
              <HookForm.Group control={control} name="price" className="mb-3">
                <HookForm.Label required>Price</HookForm.Label>
                <HookForm.NumberInput
                  placeholder="Purchase price"
                  control={control}
                  name="price"
                />
              </HookForm.Group>
              <HookForm.Group
                control={control}
                name="downpayment"
                className="mb-3"
              >
                <HookForm.Label required>Downpayment</HookForm.Label>
                <PriceInput
                  placeholder="Downpayment amount"
                  control={control}
                  name="downpayment"
                  modes={downpaymentModes}
                  defaultMode="flat"
                  summaryLabel="Amount"
                />
              </HookForm.Group>
              <HookForm.Group control={control} name="earnest" className="mb-3">
                <HookForm.Label required>Earnest</HookForm.Label>
                <HookForm.NumberInput
                  placeholder="Earnest money"
                  control={control}
                  name="earnest"
                />
              </HookForm.Group>
              <HookForm.Group
                control={control}
                name="sellerCredit"
                className="mb-3"
              >
                <HookForm.Label required>Seller credit</HookForm.Label>
                <HookForm.NumberInput
                  placeholder="Seller credit"
                  control={control}
                  name="sellerCredit"
                />
              </HookForm.Group>
              <HookForm.Group
                control={control}
                name="fundingDate"
                className="mb-3"
              >
                <HookForm.Label required>Funding date</HookForm.Label>
                <HookForm.TextInput
                  control={control}
                  name="fundingDate"
                  type="date"
                />
              </HookForm.Group>
              <HookForm.Group
                control={control}
                name="annualInsurance"
                className="mb-3"
              >
                <HookForm.Label required>Property insurance</HookForm.Label>
                <PriceInput
                  placeholder="Insurance amount"
                  control={control}
                  name="annualInsurance"
                  modes={insuranceModes}
                  defaultMode="monthly"
                />
              </HookForm.Group>
              <HookForm.Group
                control={control}
                name="annualHOA"
                className="mb-3"
              >
                <HookForm.Label required>HOA fee</HookForm.Label>
                <PriceInput
                  placeholder="HOA amount"
                  control={control}
                  name="annualHOA"
                  modes={hoaModes}
                  defaultMode="monthly"
                />
              </HookForm.Group>
            </Card.Body>
            <Card.Footer>
              <Button icon={TbPlus} type="submit">
                Create
              </Button>
            </Card.Footer>
          </Card>
        </form>
      </Container>
    </AppPage>
  );
};
